'use client';

import { useEffect, useRef } from 'react';

import type { PostSearchItem } from '@ps/api/types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@ps/ui/atoms/dialog';
import { Loader2 } from 'lucide-react';

import { PostChatMessageView } from '../../../../posts/post-card/PostChatMessageView';
import { useCreatorPostsPagination } from './useCreatorPostsPagination';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  storeId: string | null | undefined;
  onSelect: (post: PostSearchItem) => void;
}

/**
 * The "Select a Post" modal triggered from the composer's `+` menu.
 * Renders the creator's published posts as a vertical list of post
 * cards (the same `PostChatMessageView` the bubble uses), with
 * infinite scroll via an IntersectionObserver on a bottom sentinel.
 *
 * Picking a post closes the modal and emits the picked `PostSearchItem`
 * via `onSelect` — the composer then snapshots it into a
 * `CommunityChannelMessagePostLink` and pushes it onto its staged-links list.
 */
export function PostPickerDialog({
  open,
  onOpenChange,
  storeId,
  onSelect,
}: Props) {
  // `enabled: open` — the hook fetches page 1 only while the picker is open
  // (and re-fetches on each re-open, so a freshly-published post shows up),
  // so nothing loads until the creator actually opens the picker.
  const { state, loadMore, refresh } = useCreatorPostsPagination(storeId, open);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="text-content-1 border-line bg-surface-1 flex max-h-[85vh] max-w-[calc(100%-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
        <header className="border-line sticky top-0 z-10 flex flex-col items-center gap-1 border-b bg-inherit px-4 py-4">
          <DialogTitle className="text-content-1 text-base font-semibold">
            Select a Post
          </DialogTitle>
          <DialogDescription className="text-content-3 text-center text-xs">
            Attach a post to your message by selecting one from below
          </DialogDescription>
        </header>
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {state.isInitialLoading ? (
            <CenteredSpinner />
          ) : state.error && state.posts.length === 0 ? (
            <ErrorState message={state.error.message} onRetry={refresh} />
          ) : state.posts.length === 0 ? (
            <EmptyState />
          ) : (
            <ul className="flex flex-col gap-3">
              {state.posts.map(post => (
                <li key={post.id}>
                  <button
                    type="button"
                    className="focus-visible:ring-brand block w-full overflow-hidden rounded-2xl text-left transition focus-visible:outline-none focus-visible:ring-2"
                    onClick={() => {
                      onOpenChange(false);
                      onSelect(post);
                    }}
                  >
                    <PostChatMessageView post={post} fullWidth />
                  </button>
                </li>
              ))}
              {state.hasMore ? (
                <li>
                  <LoadMoreSentinel
                    onIntersect={loadMore}
                    loading={state.isLoadingMore}
                  />
                </li>
              ) : null}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CenteredSpinner() {
  return (
    <div className="flex h-40 items-center justify-center">
      <Loader2 className="text-content-4 size-5 animate-spin" />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-content-3 flex h-40 items-center justify-center text-sm">
      No published posts yet.
    </div>
  );
}

function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="flex h-40 flex-col items-center justify-center gap-2 text-center">
      <p className="text-sm text-red-300">Couldn&rsquo;t load posts.</p>
      <p className="text-content-4 text-xs">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="text-brand-text hover:text-brand-hover text-xs"
      >
        Try again
      </button>
    </div>
  );
}

function LoadMoreSentinel({
  onIntersect,
  loading,
}: {
  onIntersect: () => void;
  loading: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) onIntersect();
      },
      { rootMargin: '200px 0px 0px 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [onIntersect]);
  return (
    <div
      ref={ref}
      className="flex items-center justify-center py-2"
      aria-hidden={!loading}
    >
      {loading ? (
        <Loader2 className="text-content-4 size-4 animate-spin" />
      ) : (
        <span className="block size-4" />
      )}
    </div>
  );
}
